import React, { useEffect, useState } from "react";
import { Radar, Gift, Rocket, Check, Sparkles, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = {
  ws_ai_indicator: {
    title: "WS AI Indicator",
    eyebrow: "On-chart intelligence",
    Icon: Radar,
    accent: "linear-gradient(135deg,#38bdf8,#2563eb)",
    accentSoft: "rgba(56,189,248,0.14)",
    accentBorder: "rgba(56,189,248,0.32)",
    desc: "An AI-powered trading indicator that reads market structure, spots high-probability setups and fires real-time buy/sell signals — right on your charts.",
    bullets: [
      "Real-time buy / sell signals on any pair or timeframe",
      "Auto-detected support, resistance & liquidity zones",
      "Confidence score + suggested SL/TP for every setup",
      "One-tap send straight to your WS Journal",
    ],
  },
  ws_giveaway_zone: {
    title: "WS Giveaway Zone",
    eyebrow: "Win while you trade",
    Icon: Gift,
    accent: "linear-gradient(135deg,#f7d774,#d99a2e)",
    accentSoft: "rgba(230,194,90,0.14)",
    accentBorder: "rgba(230,194,90,0.32)",
    desc: "Weekly cash prizes, gadgets & VIP perks — win big just for trading and journaling with Wealth Secret.",
    bullets: [
      "Weekly cash & prize giveaways",
      "Earn entries by journaling and hitting streaks",
      "Exclusive gadgets, courses & VIP perks",
      "Winners announced live in the community",
    ],
  },
};

const ComingSoon = ({ feature }) => {
  const f = FEATURES[feature] || FEATURES.ws_ai_indicator;
  const [wl, setWl] = useState({ count: 0, joined: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/waitlist/${feature}`, { credentials: "include" })
      .then((r) => r.json()).then(setWl).catch(() => {});
  }, [feature]);

  const join = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/waitlist/${feature}`, { method: "POST", credentials: "include" });
      if (r.ok) setWl(await r.json());
    } catch { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="max-w-[760px] mx-auto px-8 py-10" data-testid={`coming-soon-${feature.replace(/_/g, "-")}`}>
      <div className="ws-cs-hero ws-fade-up" style={{ background: f.accentSoft, borderColor: f.accentBorder }}>
        <span className="ws-cs-icon" style={{ background: f.accent }}><f.Icon className="w-8 h-8" /></span>
        <span className="ws-cs-badge"><Clock className="w-3 h-3" /> COMING SOON</span>
        <div className="text-[11px] tracking-[0.24em] text-[color:var(--ws-muted)] uppercase mt-4">{f.eyebrow}</div>
        <h1 className="ws-heading text-[34px] font-bold text-white mt-1">{f.title}</h1>
        <p className="text-[14px] text-[color:var(--ws-muted)] leading-relaxed mt-3 max-w-[560px] mx-auto">{f.desc}</p>
      </div>

      <div className="ws-panel p-6 mt-5 ws-fade-up" style={{ animationDelay: "80ms" }}>
        <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-[color:var(--ws-blue-bright)]" /><h3 className="ws-heading text-[15px] font-semibold">What's coming</h3></div>
        <div className="grid sm:grid-cols-2 gap-3">
          {f.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-2.5 ws-price-cell p-3 rounded-xl">
              <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#34d399" }} />
              <span className="text-[13px] text-white leading-snug">{b}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="ws-panel p-6 mt-5 text-center ws-fade-up" style={{ animationDelay: "120ms" }}>
        <div className="ws-cs-count" data-testid="cs-count">
          <Rocket className="w-4 h-4 text-[color:var(--ws-cyan)]" />
          <span><b className="text-white">{(wl.count || 0).toLocaleString()}</b> traders have requested this</span>
        </div>
        {wl.joined ? (
          <div className="ws-cs-joined" data-testid="cs-joined">
            <Check className="w-4 h-4" /> You're on the wishlist — we'll notify you the moment it launches!
          </div>
        ) : (
          <button onClick={join} disabled={busy} className="ws-btn-primary px-8 py-3 rounded-xl text-[14px] font-semibold inline-flex items-center justify-center gap-2 mt-4" data-testid="join-wishlist-btn">
            <Rocket className="w-4 h-4" /> {busy ? "Joining…" : "Join the Wishlist"}
          </button>
        )}
        <p className="text-[11px] text-[color:var(--ws-muted)] mt-3">Be first in line — early members get priority access at launch.</p>
      </div>
    </div>
  );
};

export default ComingSoon;
